import { createFileRoute, Link, Outlet } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { getAdminSession } from "@/lib/admin.functions";

export const Route = createFileRoute("/admin")({
  staticData: { sitemap: "exclude-subtree" },
  head: () => ({
    meta: [
      { title: "Content admin | factory.online" },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: AdminLayout,
});

const NAV = [
  { to: "/admin", label: "Overview", exact: true },
  { to: "/admin/content/blog", label: "Blog" },
  { to: "/admin/categories", label: "Categories" },
  { to: "/admin/content/news", label: "News" },
  { to: "/admin/content/case", label: "Cases" },
  { to: "/admin/content/industry", label: "Industries" },
  { to: "/admin/content/integration", label: "Integrations" },
  { to: "/admin/pricing", label: "Pricing" },
  { to: "/admin/translations", label: "Translations" },
  { to: "/admin/requests", label: "Requests" },
  { to: "/admin/users", label: "Users", adminOnly: true },
] as const;

function AdminLayout() {
  const [ready, setReady] = useState(false);
  const [signedIn, setSignedIn] = useState(false);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      setSignedIn(!!session);
      setReady(true);
    });
    supabase.auth.getSession().then(({ data }) => {
      setSignedIn(!!data.session);
      setReady(true);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  if (!ready) return <CenteredSpinner />;
  if (!signedIn) return <SignIn />;
  return <AdminShell />;
}

function CenteredSpinner() {
  return (
    <div className="grid min-h-screen place-items-center bg-background">
      <Loader2 className="size-6 animate-spin text-primary" aria-hidden />
    </div>
  );
}

function SignIn() {
  const [pending, setPending] = useState(false);

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setPending(true);
    const { error } = await supabase.auth.signInWithPassword({
      email: String(form.get("email") ?? ""),
      password: String(form.get("password") ?? ""),
    });
    setPending(false);
    if (error) toast.error(error.message);
  }

  return (
    <div className="grid min-h-screen place-items-center bg-background px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm rounded-2xl border border-border bg-card p-8"
      >
        <h1 className="font-display text-2xl font-bold">Content admin</h1>
        <p className="mt-2 text-sm text-muted-foreground">Sign in to manage factory.online.</p>
        <div className="mt-6 grid gap-4">
          <div className="grid gap-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" name="email" type="email" required autoComplete="email" />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              required
              autoComplete="current-password"
            />
          </div>
        </div>
        <Button type="submit" className="mt-6 w-full" disabled={pending}>
          {pending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
          Sign in
        </Button>
      </form>
    </div>
  );
}

function AdminShell() {
  const session = useServerFn(getAdminSession);
  const { data, isLoading } = useQuery({
    queryKey: ["admin-session"],
    queryFn: () => session({}),
  });

  if (isLoading) return <CenteredSpinner />;

  if (!data?.isStaff) {
    return (
      <div className="grid min-h-screen place-items-center bg-background px-4 text-center">
        <div className="max-w-md">
          <h1 className="font-display text-2xl font-bold">No admin access</h1>
          <p className="mt-3 text-sm text-muted-foreground">
            This account is signed in but has no administrator role.
          </p>
          <Button
            variant="outline"
            className="mt-6"
            onClick={() => void supabase.auth.signOut()}
          >
            Sign out
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border">
        <div className="container-page flex flex-wrap items-center gap-4 py-4">
          <span className="font-display text-lg font-bold">
            factory<span className="text-primary">.admin</span>
          </span>
          <nav className="flex flex-wrap items-center gap-1" aria-label="Admin">
            {NAV.filter((item) => !("adminOnly" in item) || data.isAdmin).map((item) => (
              <Link
                key={item.to}
                to={item.to as never}
                activeOptions={{ exact: "exact" in item }}
                className="rounded-md px-3 py-1.5 text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
                activeProps={{ className: "bg-accent text-foreground" }}
              >
                {item.label}
              </Link>
            ))}
          </nav>
          <div className="ms-auto flex items-center gap-2">
            <Link to={"/" as never} className="text-sm text-muted-foreground hover:text-foreground">
              View site
            </Link>
            <Button size="sm" variant="outline" onClick={() => void supabase.auth.signOut()}>
              Sign out
            </Button>
          </div>
        </div>
      </header>
      <main className="container-page py-10">
        <Outlet />
      </main>
    </div>
  );
}
